import { useCallback, useEffect, useMemo, useState } from "react";
import { FiDownload, FiFilter, FiSearch, FiXCircle } from "react-icons/fi";
import Card from "components/Card";
import AppDataTable from "components/common/AppDataTable";
import AppPagination from "components/common/AppPagination";
import ListFetchErrorBanner from "components/common/ListFetchErrorBanner";
import FormDatePicker from "components/form/FormDatePicker";
import StatusBadge from "components/common/StatusBadge";
import { apiURl } from "store/actions";
import { buildQueryString } from "utils/helper";
import { api, isSuccessResponse } from "lib/api";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import swtAlert from "lib/sweetAlert";
import { formatPortalDateTime, noWrap } from "utils/formatters";
const DEFAULT_LIMIT = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const FILTER_INITIAL_VALUES = { page: 1, limit: DEFAULT_LIMIT, search: "", status: "", date: "" };

/** API `query.status` values for `/api/admin/transactions`. */
const TRANSACTION_STATUS_FILTER_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];
function mapTransactionToTableRow(transaction) {
  const firstName=transaction.user?.corporateProfile?.firstName ?? transaction.user?.individualProfile?.firstName ?? "";
  const lastName=transaction.user?.corporateProfile?.lastName ?? transaction.user?.individualProfile?.lastName ?? "";
  return {
    id: transaction.id||transaction._id,
    user_email_id: transaction.user?.email,
    user_name: [firstName, lastName].filter(Boolean).join(" ").trim(),
    project_name: transaction.project?.title,
    investment_amount: `${transaction.currency||"USD"}$ ${Number(transaction.amount).toFixed(2)}`,
    shares_count: transaction.shares_count||transaction.shares,
    stripe_payment_intent_id: transaction.stripeCheckoutSessionId,
    invested_at: formatPortalDateTime(transaction.createdAt),
    status: transaction.status,
  };
}

function TransactionStatusCell(row) {
  return <StatusBadge status={row.status} as="span" />;
}

function buildTransactionsListColumns() {
  return [
    { id: "sr_no", header: noWrap("#"), cell: (_row, _index) => _index + 1, sortable: true, width: "80px" },
    { id: "user_email_id", header: noWrap("User Email"), accessor: "user_email_id", sortable: true },
    { id: "user_name", header: noWrap("User Name"), accessor: "user_name", sortable: true },
    { id: "project_name", header: noWrap("Project"), accessor: "project_name", sortable: true },
    { id: "investment_amount", header: noWrap("Amount"), accessor: "investment_amount", sortable: true },
    { id: "shares_count", header: noWrap("Shares"), accessor: "shares_count", sortable: true },
    {
      id: "stripe_payment_intent_id",
      header: noWrap("Stripe Payment Intent ID"),
      accessor: "stripe_payment_intent_id",
      sortable: true,
    },
    { id: "invested_at", header: noWrap("Invested At"), accessor: "invested_at", sortable: true },
    { id: "status", header: noWrap("Status"), cell: TransactionStatusCell, sortable: true },
    // {
    //   id: "action",
    //   header: noWrap("Action"),
    //   cell(row) {
    //     return (
    //       <TransactionActionColumnCell
    //         row={row}
    //         openMenuId={openMenuId}
    //         onToggleId={toggleFn}
    //         onCloseMenu={closeFn}
    //       />
    //     );
    //   },
    // },
  ];
}

export default function TransactionsListTab() {
  const [data, setData] = useState({});
  const [filters, setFilters] = useState(FILTER_INITIAL_VALUES);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const { debounced: debouncedSearch, flush: flushSearch } = useDebouncedValue(searchDraft, 400);

  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    setFilters((prev) => {
      const nextSearch = debouncedSearch.trim();
      if (prev.search === nextSearch) return prev;
      return { ...prev, search: nextSearch, page: 1 };
    });
  }, [debouncedSearch]);

  const rows = useMemo(() => data?.data?.map(mapTransactionToTableRow) ?? [], [data]);
  const totalItems = useMemo(() => data?.total ?? 0, [data]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalItems / (data?.limit || filters.limit))), [totalItems, filters.limit, data]);

  useEffect(() => {
    setListError("");
  }, [filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endUrl = `${apiURl.transactions}`;
      const qs = buildQueryString(filters);
      const path = qs ? `${endUrl}?${qs}` : endUrl;
      const resp = await api.get(path);
      if(!isSuccessResponse(resp)) {
        const msg = resp?.message || "Could not load transactions.";
        setListError(msg);
        swtAlert.error("Transactions", msg);
        return;
      }
      setData(resp);
      setListError("");
    } catch (error) {
      const msg = error?.message || "Could not load transactions.";
      setListError(msg);
      swtAlert.error("Transactions", msg);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  const columns = useMemo(() => buildTransactionsListColumns(), []);

  const exportToCsv = () => {
    const headers = [
      "id",
      "user_email_id",
      "user_name",
      "project_name",
      "investment_amount",
      "shares_count",
      "stripe_payment_intent_id",
      "invested_at",
      "status",
    ];
    const csvRows = rows.map((item) =>
      headers
        .map((header) => `"${String(item[header] ?? "").replaceAll('"', '""')}"`)
        .join(",")
    );
    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "transactions-list.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const activeFilterCount = [filters.status, filters.date].filter(Boolean).length;

  const transactionsPrimaryCardAction = (
    <button
      type="button"
      onClick={exportToCsv}
      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-700 transition hover:bg-slate-50"
    >
      <FiDownload className="h-3.5 w-3.5" />
      Export CSV
    </button>
  );

  const transactionsFilterDrawer = filterDrawerOpen ? (
    <div className="mb-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <FormDatePicker
        label="Invested date"
        name="invested_at"
        value={filters.date}
        onChange={(event) => {
          setFilters({ ...filters, date: event.target.value, page: 1 });
        }}
        placeholder="Filter by date"
      />
      <div>
        <label className="mb-1 block text-xs font-semibold text-text-500" htmlFor="transaction-status-filter">
          Status
        </label>
        <select
          id="transaction-status-filter"
          value={filters.status === "" ? "all" : filters.status}
          onChange={(event) => {
            setFilters({
              ...filters,
              status: event.target.value === "all" ? "" : event.target.value,
              page: 1,
            });
          }}
          aria-label="Filter transactions by status"
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-text-700 outline-none ring-brand-600 focus:ring-2"
        >
          <option value="all">All statuses</option>
          {TRANSACTION_STATUS_FILTER_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
    </div>
  ) : null;

  return (
    <Card className="min-w-0 overflow-x-visible shadow-none">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-xs text-text-500">Total transactions: {totalItems}</p>
          </div>

          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <div className="relative min-w-[200px] flex-1 sm:max-w-sm">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-500" />
              <input
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    flushSearch();
                    setFilters((prev) => ({
                      ...prev,
                      search: searchDraft.trim(),
                      page: 1,
                    }));
                  }
                }}
                placeholder="Search transactions..."
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none ring-brand-600 focus:ring-2"
              />
            </div>
            <button
              type="button"
              aria-label="Open filters"
              aria-expanded={filterDrawerOpen}
              onClick={() => setFilterDrawerOpen((open) => !open)}
              className="relative rounded-lg border border-slate-200 p-2 text-text-700 transition hover:bg-slate-50"
            >
              <FiFilter />
              {activeFilterCount > 0 ? (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand-900 px-0.5 text-[10px] font-bold text-white">
                  {activeFilterCount}
                </span>
              ) : null}
            </button>
            {(searchDraft || filters.status || filters.date) && (
              <button
                type="button"
                aria-label="Clear filters"
                onClick={() => {
                  setSearchDraft("");
                  setFilters(FILTER_INITIAL_VALUES);
                }}
                className="rounded-lg border border-slate-200 p-2 text-text-700 transition hover:bg-slate-50"
              >
                <FiXCircle />
              </button>
            )}
            {transactionsPrimaryCardAction}
          </div>
        </div>
        {transactionsFilterDrawer}

        {listError ? (
          <ListFetchErrorBanner message={listError} onRetry={fetchData} loading={loading} />
        ) : null}

        <AppDataTable columns={columns} data={rows} rowKey="id" minWidth={1200} loading={loading} />
        <AppPagination
          currentPage={filters.page}
          totalPages={totalPages}
          onPageChange={(page) => setFilters({ ...filters, page })}
          totalItems={totalItems}
          pageSize={filters.limit}
          onPageSizeChange={(limit) => setFilters({ ...filters, limit, page: 1 })}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
    </Card>
  );
}
